import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Eye, Upload, Camera, Sparkles, Loader2, CheckCircle2, FileWarning,
  ArrowRight, Lock, RefreshCw, Scale, FileSignature, Trash2, AlertTriangle,
} from "lucide-react";

function urgencyTone(u) {
  if (u === "urgent" || u === "limitation-near") return "danger";
  return "neutral";
}

export default function NyayaDrishtiPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [quota, setQuota] = useState(null);
  const [filings, setFilings] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [usingWebcam, setUsingWebcam] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const PAGE = 20;

  const loadFilings = async (before = null) => {
    const params = { limit: PAGE };
    if (before) params.before = before;
    const { data } = await api.get("/nyaya/filings", { params });
    if (before) {
      setFilings((prev) => [...prev, ...data]);
    } else {
      setFilings(data);
    }
    setHasMore(data.length === PAGE);
  };

  const refresh = async () => {
    try {
      const [s, q] = await Promise.all([
        api.get("/nyaya/status").then((r) => r.data),
        api.get("/nyaya/quota").then((r) => r.data).catch(() => null),
      ]);
      setStatus(s);
      setQuota(q);
      if (s.enabled) await loadFilings();
      else setFilings([]);
    } catch {
      // noop
    }
  };

  useEffect(() => { refresh(); return () => stopWebcam(); /* eslint-disable-next-line */ }, []);

  const enableAddon = async () => {
    await api.post("/nyaya/enable");
    toast.success("Nyaya Drishti add-on enabled.");
    refresh();
  };

  const disableAddon = async () => {
    if (!window.confirm("Disable Nyaya Drishti add-on?")) return;
    await api.post("/nyaya/disable");
    toast.message("Nyaya Drishti add-on disabled.");
    refresh();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setScanning(true);
    setLastScan(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/nyaya/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastScan(data);
      if (data.storage_unavailable) {
        toast.warning("Filing scanned, but the image could not be stored. Defects-only mode.");
      } else {
        toast.success("Filing detected.");
      }
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Scan failed";
      if (err?.response?.status === 429) toast.error(msg);
      else toast.error(msg);
    } finally {
      setScanning(false);
    }
  };

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setUsingWebcam(true);
    } catch {
      toast.error("Camera permission denied or unavailable.");
    }
  };

  const stopWebcam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setUsingWebcam(false);
  };

  const snap = async () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob(async (blob) => {
      const file = new File([blob], "filing-capture.jpg", { type: "image/jpeg" });
      stopWebcam();
      await handleFile(file);
    }, "image/jpeg", 0.9);
  };

  const createCase = async (filingId) => {
    try {
      const { data } = await api.post("/nyaya/create-case", { filing_id: filingId });
      toast.success(data.already_linked ? "Case already linked." : "Case created from filing.");
      navigate(`/app/cases/${data.case_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create case");
    }
  };

  const draftResponse = async (filingId) => {
    setDrafting(filingId);
    try {
      const { data } = await api.post("/nyaya/draft-from-filing", { filing_id: filingId });
      toast.success(`Draft prepared: ${data.title}`);
      navigate(`/app/drafts/${data.draft_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not draft response");
    } finally {
      setDrafting(null);
    }
  };

  const deleteFiling = async (filingId) => {
    if (!window.confirm("Delete this detection? The scanned image and metadata will be removed.")) return;
    try {
      await api.delete(`/nyaya/filings/${filingId}`);
      toast.message("Detection deleted.");
      if (lastScan?.id === filingId) setLastScan(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const loadMore = async () => {
    if (!filings.length) return;
    const cursor = filings[filings.length - 1].created_at;
    await loadFilings(cursor);
  };

  if (status === null) return <div className="label-eyebrow">Loading…</div>;

  // Add-on locked screen
  if (!status.enabled) {
    return (
      <div data-testid="nyaya-locked">
        <PageHeader
          eyebrow="Nyaya Drishti · Add-on"
          title="See filings before they're filed."
          description="A vision-based legal sensing layer for Marianne. Snap a photo of a physical petition, affidavit or court bundle — Marianne extracts the case number, parties, court, dates, acts &amp; sections, and flags defects automatically."
        />
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 border border-[#E8E3F0] bg-white p-8">
            <Eye size={28} strokeWidth={1.4} className="text-[#7B56C9] mb-4" />
            <h3 className="font-serif text-3xl mb-4">What you get</h3>
            <ul className="space-y-3 text-sm">
              {[
                "AI vision scan of physical filings (envelopes, bundles, stamps, signatures)",
                "Auto-extracted case metadata: number, parties, court, date, acts & sections",
                "Document classification (civil / criminal / PIL / writ / affidavit / vakalatnama / bail …)",
                "Defect detection — missing signatures, blank pages, wrong orientation",
                "One-click conversion to a Marianne case with the image attached as evidence",
                "AI-drafted response letter / written statement from each detection",
                "Live feed of recent detections in your workspace",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-[#7B56C9] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2 border border-[#7B56C9] bg-[#FAFAF8] p-8 flex flex-col">
            <div className="label-eyebrow text-[#C5A059] mb-3">Add-on · Globally available</div>
            <div className="flex items-baseline gap-2 mb-3">
              <div className="font-serif text-6xl tracking-tight">₹1,200</div>
              <div className="text-sm text-[#4A5568]">/ month</div>
            </div>
            <div className="text-sm text-[#2A0F5A]/75 mb-6 leading-relaxed">
              Add Nyaya Drishti to your Marianne workspace. Billed in INR globally — included with any active Marianne plan.
            </div>
            <Button onClick={enableAddon} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8] mb-3" data-testid="enable-nyaya-btn">
              <Sparkles size={14} className="mr-1.5" /> Enable Nyaya Drishti
            </Button>
            <div className="flex items-center gap-1.5 text-[11px] text-[#4A5568]">
              <Lock size={12} className="text-[#7B56C9]" /> Toggle off any time from Settings.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active workspace
  return (
    <div data-testid="nyaya-workspace">
      <PageHeader
        eyebrow="Nyaya Drishti · Active"
        title="Vision-based filing detection"
        description="Snap a photo or upload an image of a physical filing. Marianne reads it, classifies it, flags defects, drafts a response — and turns it into a case."
        action={
          <div className="flex items-center gap-2">
            {quota && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 border border-[#E8E3F0] bg-white" data-testid="nyaya-quota">
                <span className="label-eyebrow !text-[10px] text-[#C5A059]">Today</span>
                <span className="font-mono">{quota.used_today}/{quota.limit}</span>
              </div>
            )}
            <button onClick={refresh} className="p-2 hover:bg-[#FAFAF8] border border-[#E8E3F0]" title="Refresh" data-testid="nyaya-refresh-btn"><RefreshCw size={14} /></button>
            <button onClick={disableAddon} className="text-xs text-[#4A5568] hover:text-red-700 px-3" data-testid="disable-nyaya-btn">Disable add-on</button>
          </div>
        }
      />

      {/* Capture controls */}
      <div className="border border-[#E8E3F0] bg-white p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">Capture a filing</div>
          {quota && quota.remaining <= 5 && (
            <div className="flex items-center gap-1.5 text-xs text-[#8a6b1f]" data-testid="nyaya-quota-warning">
              <AlertTriangle size={12} /> {quota.remaining} scans left today
            </div>
          )}
        </div>
        {!usingWebcam ? (
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-block">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="nyaya-file-input" />
              <span className={`inline-flex items-center gap-2 px-4 py-2 cursor-pointer text-sm text-[#FAFAF8] ${scanning ? "bg-[#4A5568]" : "bg-[#2A0F5A] hover:bg-[#3D1F85]"}`}>
                <Upload size={14} /> {scanning ? "Scanning…" : "Upload image"}
              </span>
            </label>
            <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={startWebcam} data-testid="nyaya-webcam-btn">
              <Camera size={14} className="mr-1.5" /> Use webcam
            </Button>
            {scanning && (
              <span className="text-sm text-[#4A5568] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Marianne is observing…</span>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-md border border-[#E8E3F0] bg-black" data-testid="nyaya-video" />
            <div className="flex gap-2">
              <Button onClick={snap} className="rounded-none bg-[#7B56C9] hover:bg-[#6844BA] text-white" data-testid="nyaya-snap-btn"><Camera size={14} className="mr-1.5" /> Capture</Button>
              <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={stopWebcam}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Last scan result */}
      {lastScan && (
        <div className="border border-[#7B56C9] bg-white p-6 mb-8" data-testid="nyaya-last-scan">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="label-eyebrow text-[#C5A059] mb-1">Detected · {new Date(lastScan.created_at).toLocaleString()}</div>
              <h3 className="font-serif text-3xl capitalize">{lastScan.document_type}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {lastScan.urgency && lastScan.urgency !== "normal" && (
                <StatusBadge tone={urgencyTone(lastScan.urgency)}>{lastScan.urgency}</StatusBadge>
              )}
              {(lastScan.defects || []).length > 0 && (
                <StatusBadge tone="warn">{lastScan.defects.length} defect{lastScan.defects.length > 1 ? "s" : ""}</StatusBadge>
              )}
              {lastScan.storage_unavailable && (
                <StatusBadge tone="warn">storage offline</StatusBadge>
              )}
            </div>
          </div>
          <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm mb-4">
            <Field label="Case No." value={lastScan.case_number} />
            <Field label="Court" value={lastScan.court_name} />
            <Field label="Petitioner" value={lastScan.petitioner} />
            <Field label="Respondent" value={lastScan.respondent} />
            <Field label="Filing date" value={lastScan.filing_date} />
            <Field label="Advocate" value={lastScan.advocate_name} />
            <Field label="Acts / Sections" value={(lastScan.acts_sections || []).join(", ")} />
          </dl>
          {(lastScan.defects || []).length > 0 && (
            <div className="border-l-2 border-[#C5A059] bg-[#C5A059]/10 px-3 py-2 mb-4 text-sm">
              <div className="font-medium flex items-center gap-1.5 mb-1"><FileWarning size={14} className="text-[#8a6b1f]" /> Defects flagged</div>
              <ul className="text-xs text-[#8a6b1f] list-disc pl-5">
                {lastScan.defects.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          )}
          {lastScan.extracted_text && (
            <details className="mb-4">
              <summary className="text-xs label-eyebrow cursor-pointer">View extracted text</summary>
              <pre className="text-xs whitespace-pre-wrap font-mono bg-[#FAFAF8] border border-[#E8E3F0] p-3 max-h-60 overflow-auto mt-2">{lastScan.extracted_text}</pre>
            </details>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => createCase(lastScan.id)} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="create-case-btn">
              <Scale size={14} className="mr-1.5" /> Create case in Marianne <ArrowRight size={14} className="ml-1.5" />
            </Button>
            <Button
              onClick={() => draftResponse(lastScan.id)}
              disabled={drafting === lastScan.id}
              variant="outline"
              className="rounded-none border-[#7B56C9] text-[#7B56C9] hover:bg-[#7B56C9] hover:text-white"
              data-testid="draft-response-btn"
            >
              {drafting === lastScan.id ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Drafting…</>
              ) : (
                <><FileSignature size={14} className="mr-1.5" /> Draft response with AI <ArrowRight size={14} className="ml-1.5" /></>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Recent detections */}
      <div>
        <div className="label-eyebrow mb-3">Recent detections</div>
        {filings.length === 0 ? (
          <EmptyState icon={Eye} title="No filings detected yet" description="Upload a photo or use the webcam to scan your first filing." />
        ) : (
          <>
            <div className="border border-[#E8E3F0] bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E8E3F0]">
                    <th className="text-left px-4 py-3 label-eyebrow">When</th>
                    <th className="text-left px-4 py-3 label-eyebrow">Type</th>
                    <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Case No.</th>
                    <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Court</th>
                    <th className="text-left px-4 py-3 label-eyebrow">Defects</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filings.map((f) => (
                    <tr key={f.id} className="border-b border-[#E8E3F0] hover:bg-[#FAFAF8]" data-testid={`filing-row-${f.id}`}>
                      <td className="px-4 py-3 text-xs text-[#4A5568]">{new Date(f.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 capitalize">{f.document_type}</td>
                      <td className="px-4 py-3 hidden md:table-cell font-mono text-xs">{f.case_number || "—"}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs">{f.court_name || "—"}</td>
                      <td className="px-4 py-3">{(f.defects || []).length ? <StatusBadge tone="warn">{f.defects.length}</StatusBadge> : <span className="text-xs text-[#4A5568]">none</span>}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => draftResponse(f.id)}
                            disabled={drafting === f.id}
                            className="text-xs underline text-[#7B56C9] hover:text-[#2A0F5A] disabled:opacity-50"
                            data-testid={`draft-response-row-${f.id}`}
                            title="Draft an AI response to this filing"
                          >
                            {drafting === f.id ? "Drafting…" : "Draft response"}
                          </button>
                          {f.case_id ? (
                            <Link to={`/app/cases/${f.case_id}`} className="text-xs underline text-[#2A0F5A]">Open case</Link>
                          ) : (
                            <button onClick={() => createCase(f.id)} className="text-xs underline text-[#2A0F5A] hover:text-[#7B56C9]" data-testid={`create-case-row-${f.id}`}>Create case</button>
                          )}
                          <button
                            onClick={() => deleteFiling(f.id)}
                            className="text-[#4A5568] hover:text-red-700"
                            title="Delete detection"
                            data-testid={`delete-filing-${f.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={loadMore} data-testid="nyaya-load-more-btn">
                  Load older detections
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow mb-0.5">{label}</div>
      <div className="text-sm">{value || <span className="italic text-[#4A5568]">—</span>}</div>
    </div>
  );
}
